import { useStaticQuery, graphql } from 'gatsby';
import { useMergePrismicPreviewData } from 'gatsby-plugin-prismic-previews';
import useLocalStorage from 'use-local-storage';

export function useFacilityDataWizard() {
  const facilityGroupData = useMergePrismicPreviewData(
    useStaticQuery(graphql`
      {
        allPrismicFacilityGroup {
          edges {
            node {
              _previewable
              data {
                facilities {
                  slug
                  sfdc
                  id
                  address {
                    richText
                  }
                  brand_name {
                    richText
                  }
                  calendly {
                    url
                  }
                  city {
                    richText
                  }
                  country {
                    richText
                  }
                  dine_in {
                    richText
                  }
                  d2c_url {
                    url
                  }
                  facility_description {
                    richText
                  }
                  facility_headline {
                    richText
                  }
                  kitchen_size_range {
                    richText
                  }
                  latitude {
                    richText
                  }
                  license_fee {
                    richText
                  }
                  longitude {
                    richText
                  }
                  onsite_parking {
                    richText
                  }
                  renders {
                    document {
                      ... on PrismicFacilityImages {
                        id
                        _previewable
                        data {
                          images {
                            image_url {
                              url
                            }
                          }
                        }
                      }
                    }
                  }
                  state {
                    richText
                  }
                }
                kitchen_layout_module {
                  document {
                    ... on PrismicKitchenLayoutModule {
                      id
                      _previewable
                      data {
                        additional_storage {
                          richText
                        }
                        amenity_list {
                          amenity_icon {
                            url
                          }
                          description {
                            richText
                          }
                          item {
                            richText
                          }
                        }
                        compare_after {
                          image {
                            url
                          }
                        }
                        compare_before {
                          url
                        }
                        equipment_provided {
                          item {
                            richText
                          }
                        }
                        infrastructure_icon {
                          url
                        }
                        infrastructure_list {
                          list_detail {
                            richText
                          }
                        }
                        infrastructure_title {
                          richText
                        }
                        services_icon {
                          url
                        }
                        services_list {
                          list_detail {
                            richText
                          }
                        }
                        services_title {
                          richText
                        }
                        space_amenities_title {
                          richText
                        }
                        space_icon {
                          url
                        }
                        space_list {
                          list_detail {
                            richText
                          }
                        }
                        utility_icon {
                          url
                        }
                        utility_list {
                          list_detail {
                            richText
                          }
                        }
                        utility_title {
                          richText
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          nodes {
            tags
          }
        }
      }
    `)
  );

  let selectOptions: { value: string; label: string }[] = [];

  function parseFacilityData(fgData) {
    const allData = fgData.allPrismicFacilityGroup.edges;
    const allAreas = fgData.allPrismicFacilityGroup.nodes;

    const cleanData = {};

    allData.forEach((group, index) => {
      allAreas[index].tags.forEach((tag) => {
        const trimmedTag = tag.trim();

        if (tag !== 'Facility Page') {
          if (!cleanData[trimmedTag]) {
            cleanData[trimmedTag] = {
              facilities: [],
              layout: group.node.data.kitchen_layout_module?.document?.data,
            };
            selectOptions.push({ value: trimmedTag, label: trimmedTag });
          }

          group.node.data.facilities.forEach((fac) => {
            cleanData[trimmedTag].facilities.push(fac);
          });
        }
      });
    });

    selectOptions = selectOptions.sort(
      (a, b) => a.value?.localeCompare(b.value)
    );
    return cleanData;
  }

  function parseClusterData(cleanData) {
    const clusterData: { name: string; latitude: number; longitude: number }[] =
      [];
    for (const facilityName in cleanData) {
      const region = cleanData[facilityName].facilities;
      const lngs: number[] = [];
      const lats: number[] = [];
      for (const key in region) {
        const facility = region[key];
        lngs.push(parseFloat(facility.longitude.richText[0].text));
        lats.push(parseFloat(facility.latitude.richText[0].text));
      }
      const lngAverage = lngs.reduce((a, b) => a + b, 0) / lngs.length;
      const latAverage = lats.reduce((a, b) => a + b, 0) / lats.length;
      clusterData.push({
        name: facilityName,
        latitude: latAverage,
        longitude: lngAverage,
      });
    }
    return clusterData;
  }

  const cleanData = parseFacilityData(facilityGroupData);

  const clusterData = parseClusterData(cleanData);

  const [regionName, setRegionName] = useLocalStorage('select_region', '');

  const [centerPoint, setCenterPoint] = useLocalStorage('center_point', []);

  const [utmParams, setUtmParams] = useLocalStorage('utm_params', {});

  return {
    cleanData,
    clusterData,
    selectOptions,
    regionName,
    setRegionName,
    centerPoint,
    setCenterPoint,
    utmParams,
    setUtmParams,
  };
}
